import React, { useEffect, useState, useRef } from 'react';
import ChatMessage, { Message } from './ChatMessage';
import { ArrowDownIcon } from './icons';

interface ChatContainerProps {
  messages: Message[];
  isLoading: boolean;
  isAnalyzingFiles?: boolean;
  conversationId?: string;
  agentId?: string;
  modelProvider?: string;
  modelName?: string;
  modelApiVersion?: string;
  onNudgeRetryWithSearch?: () => void;
  onNudgeEnableAuto?: () => void;
  onQuotaContinue?: (fallbackModel: string) => void;
  onQuotaChooseModel?: () => void;
  onRegenerate?: () => void;
  onEditLastMessage?: (content: string) => void;
}

const ChatContainer: React.FC<ChatContainerProps> = ({ messages, isLoading, isAnalyzingFiles, conversationId, agentId, modelProvider, modelName, modelApiVersion, onNudgeRetryWithSearch, onNudgeEnableAuto, onQuotaContinue, onQuotaChooseModel, onRegenerate, onEditLastMessage }) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const checkScrollPosition = () => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      setShowScrollButton(!isAtBottom);
    }
  };

  useEffect(() => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      chatContainer.addEventListener('scroll', checkScrollPosition);
      return () => chatContainer.removeEventListener('scroll', checkScrollPosition);
    }
  }, []);

  useEffect(() => {
    checkScrollPosition();
  }, [messages]);

  const lastUserIndex = messages.map((m) => m.role).lastIndexOf('user');

  return (
    <div 
      ref={containerRef}
      style={{
        maxWidth: '800px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        position: 'relative'
      }}
    >
      {messages.map((message, index) => (
        <ChatMessage
          onRegenerate={index === messages.length - 1 && message.role === 'assistant' ? onRegenerate : undefined}
          onEdit={index === lastUserIndex ? onEditLastMessage : undefined}
          key={index}
          message={message}
          isLoading={isLoading && index === messages.length - 1}
          isLastMessage={index === messages.length - 1}
          isAnalyzingFiles={isAnalyzingFiles && index === messages.length - 1}
          conversationId={conversationId}
          agentId={agentId}
          messageIndex={index}
          modelProvider={modelProvider}
          modelName={modelName}
          modelApiVersion={modelApiVersion}
          onNudgeRetryWithSearch={onNudgeRetryWithSearch}
          onNudgeEnableAuto={onNudgeEnableAuto}
          onQuotaContinue={onQuotaContinue}
          onQuotaChooseModel={onQuotaChooseModel}
        />
      ))}

      {showScrollButton && (
        // Sticky inside the scrolling column, so it stays centred on the conversation at any width.
        <div className="cv-scroll-anchor">
          <button type="button" className="cv-scroll-btn" onClick={scrollToBottom} aria-label="Scroll to latest message">
            <ArrowDownIcon size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatContainer);